import {html, nothing, PropertyValues} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {MobxLitElement} from "@adobe/lit-mobx";

import "./SidebarOutline.js";
import "./citation-panel/CitationPanel.js";
import "./SidebarHeader.js";
import "./integration/RestoreSession.js";
import "./integration/IntegrationSignupModal.js";
import "./toggle-button/ToggleButton.js";
import "./toggle-button/SidebarCollapseButton.js";
import {sidebarStyles} from "./Sidebar.styles.js";

import Data from "../../main/data/index.js";
import {annotationUuid} from "../../main/data/model/index.js";
import UI_MODES from "./ui_modes.js";
import Project from "../../main/project.js";
import help_project_outline from "./help_project_outline.js";
import Integration from "../../integrations/integration/index.js";

// These states are only for when using the FF sidebar API
const STATES_FF_ONLY = Object.freeze({
	BLACKLISTED:     "blacklisted", // ext is blacklisted by us or the user
	LOGGED_OUT:      "logged-out",
	EXT_DISABLED:    "ext-disabled",
	EXT_UNAVAILABLE: "ext-unavailable" // ext is unavailable on this page due to browser restrictions (such as about:blank)
});

// These states apply no matter how the sidebar is added
const STATE = {
	OUTLINE:  "outline",
	CITATION: "citation",

	// Shows loading dots and nothing else in the entire sidebar
	LOADING: "loading",

	// Allows user to enter their email address to retrieve a temporary session
	RESTORE_SESSION: "restore-session",

	...STATES_FF_ONLY
};

@customElement("pn-sidebar")
export class Sidebar extends MobxLitElement {
	@property() annotation = {
		type:       "",
		ai:         false,
		renderable: true,
		pageData:   null,
		citation:   null
	};
	@property({type: Boolean}) isSidebarApiClosed = true;
	@property({type: UI_MODES}) mode: UI_MODES;
	@property({type: Boolean}) sessionCodes = false;
	@property({type: Boolean}) floating = true;
	@property({type: Boolean}) projectSelector = true;
	@property({type: Boolean}) accountMenu = true;
	@property({type: Boolean}) alwaysVisible = false;

	@state() uiState = STATE.OUTLINE;
	@state() citationOpened = {};
	@state() citationAnnotation = {};
	@state() isSidebarHidden = true;
	@state() isHelpProjectVisible = false;

	static styles = [sidebarStyles];

	willUpdate(changedProperties: PropertyValues<this>) {
		if (
			this.isSidebarHidden
			&& changedProperties.has("alwaysVisible")
			&& (changedProperties.get("alwaysVisible") ?? this.alwaysVisible)
		)
			this.isSidebarHidden = false;
	}

	connectedCallback() {
		super.connectedCallback();

		// This catches events from the comment menu and the sidebar
		window.addEventListener("pnOpenSidebar", this.openSidebar);
		window.addEventListener("pnClickCitationButton", this.onClickCitationButton);
		window.addEventListener("pnShowHelpProject", this.onClickShowHelpProject);
		window.addEventListener("pnDisable", this.closeSidebar);
	}

	disconnectedCallback() {
		window.removeEventListener("pnOpenSidebar", this.openSidebar);
		window.removeEventListener("pnClickCitationButton", this.onClickCitationButton);
		window.removeEventListener("pnShowHelpProject", this.onClickShowHelpProject);
		window.removeEventListener("pnDisable", this.closeSidebar);
		super.disconnectedCallback();
	}

	openSidebar = () => {
		if (this.isSidebarHidden)
			this.onShowHideSidebar();
	};

	closeSidebar = () => {
		if (!this.isSidebarHidden)
			this.onShowHideSidebar();
	};

	onClickShowHelpProject = () => {
		this.openSidebar();
		this.isHelpProjectVisible = true;
	};

	hasEditAccess = () => {
		if (this.isHelpProjectVisible)
			return false;

		const account = Data.app.me;
		const project = Data.app.project;

		if (!account || !project)
			return false;

		return Project.hasAccess(account, project, Project.ACCESS_EDIT);
	};

	onClickCitationButton = (event: CustomEvent<{annotationUuid: annotationUuid}>) => {
		const annotationUuid = event.detail.annotationUuid;
		const annotation = Data.app.outline
			?.flatMap(section => section.annotations)
			.find(a => a.uuid === annotationUuid);
		this.citationAnnotation = Object.assign({}, annotation);
		this.citationOpened = Object.assign({}, annotation?.citation ?? {});
		this.uiState = STATE.CITATION;
		if (this.isSidebarHidden)
			this.onShowHideSidebar();
	};

	onCloseCitationPanel = () => {
		this.uiState = STATE.OUTLINE;
		this.citationOpened = {};
	};

	onClickRestoreSession = () => {
		this.uiState = STATE.RESTORE_SESSION;
	};

	onCloseRestoreSession = () => {
		this.uiState = STATE.OUTLINE;
	};

	onShowHideSidebar = () => {
		if (this.isSidebarHidden && !Data.app.isLoggedIn && Integration.isIntegration()) {
			this.dispatchEvent(new CustomEvent("pnEnable", {
				bubbles:  true,
				composed: true
			}));

			// If it's the first time opening the sidebar, and there's no account, create a temp integration account.
			(async () => {
				await new Promise(r => setTimeout(r, 2000));
				if (Data.app.isLoggedIn)
					return;
				await Data.app.createIntegrationAccount(Integration.name!, Integration.getClientToken);
				Integration.gtag("event", "init_temp_account");
			})();
		}

		this.isSidebarHidden = !this.isSidebarHidden;
	};

	onToggleHelpProject = () => {
		this.isHelpProjectVisible = !this.isHelpProjectVisible;
	};

	renderRestoreSession() {
		if (this.uiState !== STATE.RESTORE_SESSION)
			return nothing;

		return html`
			<pn-restore-session
				@closeRestoreSession="${this.onCloseRestoreSession}"
			></pn-restore-session>
		`;
	}

	renderCitationPanel() {
		if (!Data.app.isLoggedIn || !Data.app.project)
			return nothing;

		return html`
			<pn-citation-panel
				.annotation="${this.citationAnnotation}"
				.citation="${this.citationOpened}"
				@closeCitationPanel="${this.onCloseCitationPanel}"
				.editable="${true}"
				.isAutoCitationsDisabled="${false}"
				.renderModified="${false}"
				.project="${Data.app.project}"
			></pn-citation-panel>
		`;
	}

	renderToggleButton() {
		if (!this.floating || this.alwaysVisible)
			return nothing;

		return html`
			<pn-toggle-button
				.isSidebarHidden=${this.isSidebarHidden}
				@click=${this.onShowHideSidebar}
			/>
		`;
	}

	renderHeader() {
		if (this.uiState === STATE.CITATION)
			return nothing;

		return html`
			<pn-sidebar-header
				.isLoggedIn=${Data.app.isLoggedIn}
				.sessionCodes=${this.sessionCodes}
				.floating=${this.floating}
				.accountMenu=${this.accountMenu}
				.projectSelector=${this.projectSelector}
				.isHelpProjectVisible=${this.isHelpProjectVisible}
				@pnToggleHelpProject=${this.onToggleHelpProject}
				@clickRestoreSession="${this.onClickRestoreSession}"
				@showHideSidebar="${this.onShowHideSidebar}"
			/>
		`;
	}

	renderSkipLinks() {
		const jumpToMainContent = () => {
			// Focus the components, which will delegate focus to the first
			// focusable element within
			let elem;
			if (this.uiState === STATE.OUTLINE)
				elem = this.renderRoot.querySelector("pn-sidebar-outline")?.shadowRoot?.querySelector("pn-outline-section");
			else if (this.uiState === STATE.CITATION)
				elem = this.renderRoot.querySelector("pn-citation-panel");

			(elem as HTMLElement)?.focus();
		};

		const jumpToCollapseButton = () => {
			(this.renderRoot.querySelector("pn-sidebar-collapse-button") as HTMLElement)?.focus();
		};

		// Use buttons because links to page sections don't work if the sections are in the shadow DOM
		return html`<div id="skip-links-container">
			<button @click=${() => jumpToMainContent()} type="button">Skip to main content</button>
			<!-- <button type="button">Skip to help menu</button> -->
			<button @click=${() => jumpToCollapseButton()} type="button">Skip to collapse button</button>
		</div>`;
	}

	renderContent() {
		if (this.uiState === STATE.LOADING || !Data.app.isLoggedIn || Data.app.isLoggingIn) {
			return html`
				<div
					class="${classMap({
						"sidebar-content":          true,
						[`state-${STATE.LOADING}`]: true
					})}"
				>
					<pn-loading-box/>
				</div>
			`;
		}

		return html`
			<div
				class="${classMap({
					"sidebar-content":         true,
					[`state-${this.uiState}`]: true,
					"hidden":                  this.isSidebarHidden
				})}"
			>
				<pn-sidebar-outline
					.isEditable=${this.hasEditAccess()}
					.mode=${this.isHelpProjectVisible ? UI_MODES.SIDEBAR_READONLY : this.mode}
					.floating=${this.floating}
					.forceOutline=${this.isHelpProjectVisible ? help_project_outline : false}
				></pn-sidebar-outline>
	
				${this.renderCitationPanel()}
				${this.renderRestoreSession()}
			</div>
		`;
	}

	renderSidebar() {
		if (this.floating && (!this.alwaysVisible && this.isSidebarHidden))
			return nothing;

		switch (this.mode) {
			case UI_MODES.SIDEBAR:
			case UI_MODES.SIDEBAR_DRAG:
			case UI_MODES.SIDEBAR_READONLY:
				break;
			default:
				return "Unsupported outline mode: " + this.mode;
		}

		const isIntegration = Integration.isIntegration();

		return html `
			<aside
				class="${classMap({
					"is-floating":             this.floating,
					"is-integration":          isIntegration,
					"custom-scroll-bar-light": isIntegration,
					"is-int-sidebar-hidden":   this.isSidebarHidden
				})}"
			>
				${this.renderSkipLinks()}
				${this.renderHeader()}
				${this.renderContent()}
				${this.floating && !this.alwaysVisible ? html`
					<pn-sidebar-collapse-button
						@click=${this.onShowHideSidebar}
					/>
				` : nothing}
			</aside>
		`;
	}

	render() {
		return html`
			${this.renderToggleButton()}
			${this.renderSidebar()}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"pn-sidebar": Sidebar;
	}
}
